import React, { Suspense, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ChevronDown, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const NearbyMiniMap = React.lazy(() => import('./NearbyMiniMap'));

const therapyLabel = (t) => t.replace(/-/g, ' ').replace(/\b(iv|hbot|nad|pemf|trt|prp)\b/gi, (m) => m.toUpperCase());

export const NearbyRecovery = ({ gymId, metro }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('idle');
  if (!metro) return null;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && status === 'idle') {
      setStatus('loading');
      fetch(`${API}/api/directory/near-gym/${gymId}`)
        .then((r) => (r.ok ? r.json() : Promise.reject()))
        .then((d) => { setData(d); setStatus('done'); })
        .catch(() => setStatus('error'));
    }
  };

  const hasMap = data?.gym?.lat && data?.facilities?.some((f) => f.lat);

  return (
    <div className="mt-3 pt-3 border-t border-zinc-800/70" data-testid={`nearby-recovery-${gymId}`}>
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between gap-2 text-left group"
        data-testid={`nearby-recovery-toggle-${gymId}`}
      >
        <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-zinc-500 group-hover:text-[#C7FA3E] transition-colors">
          <MapPin className="w-3.5 h-3.5 text-[#C7FA3E]" /> Recovery near this gym
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-zinc-600 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="mt-3" data-testid={`nearby-recovery-panel-${gymId}`}>
          {status === 'loading' && <p className="text-zinc-600 text-xs">Finding recovery clinics nearby…</p>}
          {status === 'error' && (
            <Link to={`/locator/${metro.slug}`} className="text-[#C7FA3E] text-xs hover:underline">
              Browse vetted recovery clinics in {metro.name} →
            </Link>
          )}
          {status === 'done' && data && (
            <>
              {hasMap && (
                <Suspense fallback={<div className="w-full bg-zinc-900" style={{ height: 220 }} />}>
                  <NearbyMiniMap gym={data.gym} facilities={data.facilities} />
                </Suspense>
              )}
              <div className="mt-2 space-y-1.5">
                {data.facilities.map((f) => (
                  <Link
                    key={f.slug}
                    to={f.url}
                    className="flex items-center justify-between gap-2 px-2.5 py-2 bg-black/40 border border-zinc-800 hover:border-[#C7FA3E]/50 transition-colors group"
                    data-testid={`nearby-facility-${f.slug}`}
                  >
                    <span className="min-w-0">
                      <span className="block text-white text-xs font-semibold truncate group-hover:text-[#C7FA3E] transition-colors">{f.name}</span>
                      <span className="block text-zinc-500 text-[10px] truncate">
                        {f.therapies.map(therapyLabel).join(' · ')}{f.city ? ` · ${f.city}` : ''}
                      </span>
                    </span>
                    <span className="flex items-center gap-1 text-zinc-500 text-[10px] whitespace-nowrap">
                      {f.distance_mi != null && <>{f.distance_mi} mi</>}
                      <ArrowRight className="w-3 h-3 text-[#C7FA3E]" />
                    </span>
                  </Link>
                ))}
                {data.facilities.length === 0 && (
                  <p className="text-zinc-600 text-xs">No geocoded clinics nearby yet.</p>
                )}
              </div>
              <Link
                to={`/locator/${metro.slug}`}
                className="inline-block mt-2 text-[#C7FA3E] text-[11px] uppercase tracking-wider hover:underline"
                data-testid={`nearby-browse-all-${gymId}`}
              >
                All {metro.name} recovery clinics →
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  );
};
